/**
 * src/lib/timezone.js — wall-clock time in an arbitrary zone, as a unix
 * timestamp, and back.
 *
 * Duplicates api/src/lib/timezone.js's conversion rather than sharing it: the
 * API and SPA are separate bundles with separate package.json files and no
 * shared-package tooling in this repo, so copying ~15 lines is simpler than
 * introducing a workspace for one small utility. Keep the two in sync if
 * either changes.
 */

/**
 * Converts a wall-clock date and time in a given IANA zone to a unix
 * timestamp. See the API-side copy of this function for the method.
 *
 * @param {string} dateStr `YYYY-MM-DD`.
 * @param {string} timeStr `HH:MM`.
 * @param {string} timeZone IANA zone name.
 * @returns {number} Unix seconds.
 */
export function zonedTimeToUnix(dateStr, timeStr, timeZone) {
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hour, minute] = timeStr.split(':').map(Number);
  const naiveUtcMillis = Date.UTC(year, month - 1, day, hour, minute);

  const offsetMinutes = timezoneOffsetMinutes(timeZone, naiveUtcMillis);
  return (naiveUtcMillis - offsetMinutes * 60_000) / 1000;
}

/**
 * The zone's offset from UTC, in minutes, at a given instant.
 *
 * @param {string} timeZone
 * @param {number} utcMillis
 * @returns {number} Minutes.
 */
function timezoneOffsetMinutes(timeZone, utcMillis) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(utcMillis));

  const get = (type) => Number(parts.find((p) => p.type === type).value);
  const hour = get('hour') % 24;
  const asUtcMillis = Date.UTC(get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second'));

  return (asUtcMillis - utcMillis) / 60_000;
}

/**
 * Breaks a unix timestamp into its wall-clock date/time parts in a zone.
 *
 * @param {number} unixSeconds
 * @param {string} timeZone
 * @returns {{ year: number, month: number, day: number, hour: number, minute: number, weekday: number }}
 *   `month` and `day` are 1-based; `weekday` is 0 (Sunday) through 6.
 */
export function zonedParts(unixSeconds, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
  }).formatToParts(new Date(unixSeconds * 1000));

  const get = (type) => parts.find((p) => p.type === type).value;
  const weekdayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number(get('hour')) % 24,
    minute: Number(get('minute')),
    weekday: weekdayNames.indexOf(get('weekday')),
  };
}
